import { useTranslation } from "react-i18next";

import { Typography } from "@mui/material";
import { memo, useEffect } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { Box } from "../../common/components";
import { useTimeSince } from "../../common/hooks/useTimeSince";
import { createStyles, palette, spacing } from "../../styles/theme";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import { ProjectWithDesigns } from "../../types";
import { ROUTES } from "../../constant/common.constant";
import { mergeSx } from "../../utils/themeUtils";
import ProjectCardMenu from "./ProjectCardMenu";
import { useTeam } from "../../context/Team";
import { sortDesigns } from "../../utils/SortingUtils";
import {
  DND_ITEM_TYPES,
  DraggedProjectItem,
} from "../../constant/dnd.constant";

export const PROJECT_CARD_MOBILE_MIN_WIDTH = "276px";
export const PROJECT_CARD_MOBILE_MAX_WIDTH = "572px";
export const PROJECT_CARD_DESKTOP_MIN_WIDTH = "276px";
export const PROJECT_CARD_DESKTOP_MAX_WIDTH = "572px";

const styles = createStyles({
  container: {
    background: palette.grey.lightBackground,
    borderRadius: "12px",
    minWidth: {
      xs: PROJECT_CARD_MOBILE_MIN_WIDTH,
      lg: PROJECT_CARD_DESKTOP_MIN_WIDTH,
    },
    maxWidth: {
      xs: PROJECT_CARD_MOBILE_MAX_WIDTH,
      lg: PROJECT_CARD_DESKTOP_MAX_WIDTH,
    },
    p: 2,
    ":hover": {
      boxShadow: `0 0 0 1px ${palette.grey.midGrey}`,
    },
    ":hover:has(.design:hover, .openProjectTile:hover)": {
      boxShadow: "none",
    },
    ":active": {
      boxShadow: `0 0 0 1px ${palette.brand.secondary}`,
    },
    ":active:has(.projectCardMenu *:active)": {
      boxShadow: "none",
    },
    cursor: "pointer",
  },
  designContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    mb: 2,
  },
  design: {
    flexBasis: `calc(50% - 6px)`, //6px represent the half of the gap value, without the calc the box is to big
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    minHeight: "90px",
    maxHeight: "90px",
    minWidth: "120px",
    padding: "8px",
    background: "white",
    borderRadius: "8px",
    userSelect: "none",
    cursor: "pointer",
    ":hover": {
      boxShadow: `0 0 0 1px ${palette.grey.midGrey}`,
      "& .designName": {
        display: "block",
      },
    },
    ":active": {
      boxShadow: `0 0 0 1px ${palette.brand.secondary}`,
      "& .designName": {
        display: "block",
      },
    },
  },
  blankDesign: {
    flexBasis: `calc(50% - 6px)`, //6px represent the half of the gap value, without the calc the box is to big
    minHeight: "90px",
    height: "90px",
    minWidth: "120px",
    padding: "8px",
    userSelect: "none",
  },
  viewProjectTile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "white",
    ":hover": {
      boxShadow: `0 0 0 1px ${palette.grey.midGrey}`,
      "&>span": {
        textDecoration: "underline",
      },
    },
    ":active": {
      boxShadow: `0 0 0 1px ${palette.brand.secondary}`,
    },
  },
  img: {
    maxHeight: { xs: "49px", lg: "49px" },
    userSelect: "none",
  },
});

const NUMBER_OF_DESIGN_PER_CARD = 3;

interface Props {
  project: ProjectWithDesigns;
}

const ProjectCard = ({ project }: Props) => {
  const { t } = useTranslation();
  const { teamId, team } = useTeam();
  const { timeSince } = useTimeSince(project.updatedAt);
  const navigate = useNavigate();
  const { isMobile } = useThemeBreakpoints();
  const numberOfBlankDesign =
    NUMBER_OF_DESIGN_PER_CARD - Math.min(project.designs.length, 3);

  const sortedDesigns = project?.designs.sort(sortDesigns);

  const [{ isDragging }, drag, preview] = useDrag<
    DraggedProjectItem,
    void,
    { isDragging: boolean }
  >({
    type: DND_ITEM_TYPES.PROJECT,
    item: () => {
      return {
        type: DND_ITEM_TYPES.PROJECT,
        projectId: project.id,
        projectName: project.name || t("project.project"),
        teamId: teamId || "",
        teamName: team?.name || "",
        source: "card",
        designs: sortedDesigns.slice(0, 3).map((design) => ({
          id: design.id,
          name: design.name,
          images: design.images,
        })),
      };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: (monitor) => {
      if (!teamId || !project.id) return false;

      const offset = monitor.getInitialClientOffset();
      if (!offset) return true;

      const element = document.elementFromPoint(offset.x, offset.y);
      if (!element) return true;

      return !element.closest("[data-no-drag]");
    },
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <div
      ref={isMobile ? null : (drag as any)}
      style={{
        opacity: isDragging ? 0.4 : 1,
        cursor:
          teamId && project.id ? (isDragging ? "grabbing" : "grab") : "pointer",
      }}
    >
      <Box
        sx={styles.container}
        data-test={"projectCard"}
        onClick={(e) => {
          const isModalOpen = (e.target as any).closest(".MuiDialog-container");
          if (isModalOpen) {
            return;
          }

          navigate(
            generatePath(ROUTES.PROJECT, {
              projectId: project.id,
              teamId: teamId,
            })
          );
        }}
      >
        <Box sx={styles.designContainer}>
          {sortedDesigns.slice(0, 3)?.map((design) => (
            <Box
              sx={styles.design}
              draggable={false}
              key={design.id}
              className="design"
              data-no-drag="true"
              onClick={(e) => {
                e.stopPropagation();
                navigate(
                  generatePath(ROUTES.STUDIO, {
                    projectId: project.id,
                    designId: design.id,
                    teamId: teamId,
                  })
                );
              }}
            >
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  alignItems: "center",
                  gap: spacing.smallDesktop,
                  py: "5px",
                }}
              >
                {design?.images?.map((image) => (
                  <Box
                    component={"img"}
                    draggable={false}
                    sx={{
                      ...styles.img,
                      content: `url(${image.url})`,
                      userSelect: "none",
                    }}
                    key={image.url}
                  />
                ))}
              </Box>
              <Typography
                variant="textSm"
                className="designName"
                sx={{ display: "none" }}
              >
                {design.name}
              </Typography>
            </Box>
          ))}
          <Box
            sx={mergeSx(styles.design, styles.viewProjectTile)}
            className="openProjectTile"
            onClick={() =>
              navigate(
                generatePath(ROUTES.PROJECT, {
                  projectId: project.id,
                  teamId: teamId,
                })
              )
            }
          >
            <Typography variant="textSm" color={palette.brand.secondary}>
              {t(
                project.designs.length >= 1
                  ? "project.viewAll"
                  : "project.openProject"
              )}
            </Typography>
          </Box>
          {Array(...new Array(numberOfBlankDesign)).map((_, index) => (
            <Box sx={styles.blankDesign} key={`${index}-blank`} />
          ))}
        </Box>
        <Box display="flex" flexDirection="column" gap="5px">
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box sx={{ maxWidth: "calc(100% - 24px)", width: "100%" }}>
              <Typography
                variant="textRegular"
                maxWidth="fit-content"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                display="block"
              >
                {project.name ? project.name : t("project.project")}
              </Typography>
            </Box>
            <ProjectCardMenu project={project} designs={project.designs} />
          </Box>
          <Typography variant="textXs">{timeSince}</Typography>
        </Box>
      </Box>
    </div>
  );
};

export default memo(ProjectCard);
